import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import {
  APPLICATION_DETAILS,
  APPLICATION_COMMENT,
  DOCUMENT_TYPE,
  OTHER_APPLICATION_DETAILS,
  UTILITY_CONSUMER,
} from "tagTypes";

export const useApplicationDetailsQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/power/application",
    query,
    tags: [APPLICATION_DETAILS, APPLICATION_COMMENT],
  });

export const useLazyGetPowerUtilityApplicationsQuery = () => {
  const [getPowerApplications, data] = useLazyGetQuery();
  const powerApplications = async (query = {}) =>
    await getPowerApplications(
      {
        endpoint: "/power/application",
        query,
        tags: [APPLICATION_DETAILS, APPLICATION_COMMENT],
      },
      true
    ).unwrap();
  return [powerApplications, data];
};

export const useApplicationDetailQuery = (applicationId, query = {}) =>
  useGetQuery({
    endpoint: `/power/application/${applicationId}`,
    query,
    tags: [APPLICATION_DETAILS],
  });

export const useGetUserApplicationsQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/user/application",
    query,
    tags: [APPLICATION_DETAILS],
  });

export const useGetMonthlyApplicationCountQuery = () =>
  useGetQuery({
    endpoint: "/power/application/report/monthly-application-count",
    tags: [APPLICATION_DETAILS],
  });

export const useLazyMonthlyApplicationCountQuery = () => {
  const [monthlyApplicationCount, data] = useLazyGetQuery();
  const getMonthlyApplicationCount = async () =>
    await monthlyApplicationCount(
      {
        endpoint: "/power/application/report/monthly-application-count",
        tags: [APPLICATION_DETAILS],
      },
      true
    ).unwrap();
  return [getMonthlyApplicationCount, data];
};

export const useGetApplicationRefundCalculations = (query = {}) =>
  useGetQuery({
    endpoint: "/application/refund-calculation",
    query,
    tags: [APPLICATION_DETAILS],
  });

export const useGetConsumerBankAndPaymentDetails = (
  applicationId,
  query = {}
) =>
  useGetQuery({
    endpoint: `/bank-detail/${applicationId}`,
    query,
    tags: [APPLICATION_DETAILS],
  });

export const useLazyApplicationDetailsQuery = () => {
  const [applicationDetails, data] = useLazyGetQuery();
  const applicationDetail = async (query = {}) =>
    await applicationDetails(
      {
        endpoint: "/power/application",
        query,
        tags: [APPLICATION_DETAILS, APPLICATION_COMMENT],
      },
      true
    ).unwrap();
  return [applicationDetail, data];
};

export const useLazyUserApplicationDetailsQuery = () => {
  const [applicationDetails, data] = useLazyGetQuery();
  const applicationDetail = async (query = {}) =>
    await applicationDetails({
      endpoint: "/user/application",
      query,
      tags: [APPLICATION_DETAILS],
    }).unwrap();
  return [applicationDetail, data];
};

export const useLazyApplicationDetailQuery = () => {
  const [applicationDetails, data] = useLazyGetQuery();
  const applicationDetail = async (applicationId, query = {}) =>
    await applicationDetails(
      {
        endpoint: `/power/application/${applicationId}`,
        query,
        tags: [APPLICATION_DETAILS, APPLICATION_COMMENT],
      },
      true
    ).unwrap();
  return [applicationDetail, data];
};

export const usePostApplicationDetailsMutation = () => {
  const [applicationDetails, data] = usePostMutation();
  const applicationDetail = async (payload, query = {}) =>
    await applicationDetails({
      endpoint: "/power/application",
      query,
      payload: payload || {},
      toastMessage: "Application request added successfully",
      tags: [APPLICATION_DETAILS, DOCUMENT_TYPE],
    }).unwrap();
  return [applicationDetail, data];
};

export const usePutRemoveMeterMutation = () => {
  const [removeMeter, data] = usePutMutation();
  const removeCurrentMeter = async (utilityConsumerId, query = {}) =>
    await removeMeter({
      endpoint: `/disconnect-meter/utility-consumer/${utilityConsumerId}`,
      query,
      toastMessage: "Meter removed successfully",
      tags: [APPLICATION_DETAILS, UTILITY_CONSUMER],
    }).unwrap();
  return [removeCurrentMeter, data];
};

export const usePutApplicationDetailStatusMutation = () => {
  const [applicationStatus, data] = usePutMutation();
  const status = async (payload = {}) =>
    await applicationStatus({
      endpoint: "/application/approve-application",
      payload,
      toastMessage: "Application request updated successfully",
      tags: [APPLICATION_DETAILS, OTHER_APPLICATION_DETAILS],
    }).unwrap();
  return [status, data];
};

export const usePutReleaseConnectionMutation = () => {
  const [releaseConnection, data] = usePutMutation();
  const completeApplicationProcess = async ({
    applicationId,
    payload = {},
    toastMessage,
    query = {},
  }) =>
    await releaseConnection({
      endpoint: `/application/${applicationId}/connection-released`,
      payload,
      query,
      toastMessage,
      tags: [APPLICATION_DETAILS],
    }).unwrap();
  return [completeApplicationProcess, data];
};

export const usePutSettlementRefundProcessMutation = () => {
  const [refundProcess, data] = usePutMutation();
  const settlementRefundProcess = async ({
    applicationId,
    payload = {},
    toastMessage,
    query = {},
  }) =>
    await refundProcess({
      endpoint: `/application/${applicationId}/settlement-refund-after-connection-released`,
      payload,
      query,
      toastMessage,
      tags: [APPLICATION_DETAILS],
    }).unwrap();
  return [settlementRefundProcess, data];
};

export const usePostAddUserBankInfoMutation = () => {
  const [addBankInformation, data] = usePostMutation();
  const addBankInfo = async (payload, query = {}) =>
    await addBankInformation({
      endpoint: "/bank-detail",
      query,
      payload: payload || {},
      toastMessage: "Bank information added successfully",
      tags: [APPLICATION_DETAILS],
    }).unwrap();
  return [addBankInfo, data];
};

export const usePostRefundCalculationMutation = () => {
  const [addRefundCalculations, data] = usePostMutation();
  const addRefundCalculationInfo = async (payload, query = {}) =>
    await addRefundCalculations({
      endpoint: "/refund-calculation",
      query,
      payload: payload || {},
      toastMessage: "Refund calculations added successfully",
      tags: [APPLICATION_DETAILS, UTILITY_CONSUMER],
    }).unwrap();
  return [addRefundCalculationInfo, data];
};

export const usePutRefundCalculationMutation = () => {
  const [addRefundCalculations, data] = usePutMutation();
  const addRefundCalculationInfo = async (payload, query = {}) =>
    await addRefundCalculations({
      endpoint: "/refund-calculation",
      query,
      payload: payload || {},
      toastMessage: "Refund calculations updated successfully",
      tags: [APPLICATION_DETAILS, UTILITY_CONSUMER],
    }).unwrap();
  return [addRefundCalculationInfo, data];
};

export const usePutUpdateApplicantFieldsMutation = () => {
  const [updateApplicantFields, data] = usePutMutation();
  const updateApplicant = async ({ query = {}, payload = {}, toastMessage }) =>
    await updateApplicantFields({
      endpoint: "/application/pan-gst-number-update",
      payload,
      query,
      toastMessage,
      tags: [APPLICATION_DETAILS, OTHER_APPLICATION_DETAILS],
    }).unwrap();
  return [updateApplicant, data];
};
